import React, { useEffect, useLayoutEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  IonButton,
  IonCardContent,
  IonCol,
  IonImg,
  IonInput,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonLoading,
  IonRow,
  IonText,
  IonTextarea,
} from "@ionic/react";
import { musicalNoteOutline } from "ionicons/icons";

import { useProfile } from "../data/store/useProfile";
import { useToasts } from "../data/store/useToasts";
import { useSongs } from "../data/store/useSongs";
import isEmpty from "lodash.isempty";

import Content from "./Content";
import Uploader from "./Uploader";
import ArtistSelector from "./ArtistSelector";

import "react-dropzone-uploader/dist/styles.css";

interface EditForm {}

const EditForm: React.FC<EditForm> = () => {
  const { id } = useParams<{ id: string }>();
  const { push } = useHistory();
  const { fetch, updateSong, status, songs } = useSongs();
  const { ownerId, authorId } = useProfile();
  const { post } = useToasts();
  const song = songs[id];

  const [showArt, setShowArt] = useState(false);
  const [songTitle, setSongTitle] = useState(song && song.title);
  const [songDescription, setSongDescription] = useState(
    song && song.description
  );
  const [songMedia, setSongMedia] = useState(song && song.media);
  const [songAuthors, setSongAuthors] = useState(song && song.authors);
  const [art, setArt] = useState();
  const isDisableSaveBtn =
    isEmpty(songTitle) ||
    isEmpty(songMedia.src) ||
    (showArt && isEmpty(songMedia.src));

  const handleRedirect = () => {
    push("/song/" + song.id);
  };

  const handleSetAuthors = (songAuthors: string[]) => {
    if (songAuthors.includes(authorId!)) {
      setSongAuthors(songAuthors);
    } else {
      setSongAuthors([authorId!, ...songAuthors]);
    }
  };

  const handleToggleMedia = () => {
    if (songMedia && songMedia.src) {
      setSongMedia({
        src: "",
        type: "",
      });
    } else {
      setSongMedia(song.media);
    }
  };

  useLayoutEffect(() => {
    if (!song) {
      fetch(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song]);

  useEffect(() => {
    if (song && song.authors && isEmpty(songAuthors[0])) {
      setSongAuthors(song.authors);
    }
  }, [authorId, songAuthors, song]);

  useEffect(() => {
    if (song) {
      const { title, description, media, authors, art } = song;
      setSongTitle(title);
      setSongDescription(description);
      setSongAuthors(authors);
      setSongMedia(media);
      art && setArt(art);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song]);

  if (!song || status === "fetching") {
    return <IonLoading isOpen />;
  }

  return ownerId && ownerId !== song.ownerId ? (
    <Content>
      <IonText color={"danger"}>
        You do not have permission to view the content of this page
      </IonText>
    </Content>
  ) : (
    <Content className="chill">
      <IonRow>
        <IonCol sizeMd="6" pushMd="3">
          <IonList className="inner-chill">
            {!isEmpty(songAuthors) && (
              <ArtistSelector
                placeholder={"Collaborators"}
                fieldName={"Authors"}
                members={songAuthors}
                setMembers={handleSetAuthors}
              />
            )}

            <IonItem>
              <IonLabel position="stacked" color="primary">
                Title
              </IonLabel>
              <IonInput
                spellCheck={false}
                autocapitalize="off"
                value={songTitle}
                onIonChange={(e) => setSongTitle(e.detail.value!)}
                required
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked" color="primary">
                Description
              </IonLabel>
              <IonTextarea
                rows={5}
                autoGrow
                value={songDescription}
                onIonChange={(e) => setSongDescription(e.detail.value!)}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked" color="primary">
                Media
              </IonLabel>
              <Uploader
                collection="songs"
                onToggle={handleToggleMedia}
                initialAudio={song}
                onComplete={(media) => {
                  if (media) {
                    setSongMedia(media);
                  }
                }}
              />
            </IonItem>

            <IonItem lines="none">
              {showArt && (
                <IonLabel
                  onClick={() => setShowArt(false)}
                  position="stacked"
                  color="primary"
                >
                  Art
                </IonLabel>
              )}

              {showArt ? (
                <Uploader
                  message="Select an image file"
                  collection={"covers"}
                  onComplete={(media) => {
                    if (media) {
                      setArt(media);
                    }
                  }}
                />
              ) : (
                <IonRow>
                  <IonButton
                    fill="clear"
                    expand="full"
                    onClick={() => {
                      setShowArt((prevState) => !prevState);
                    }}
                  >
                    {isEmpty(song.art) ? "Add" : "Change"} Artwork
                  </IonButton>

                  <IonCardContent className="song-item-content">
                    {song.art && (
                      <IonImg style={{ margin: "-20px" }} src={song.art.src} />
                    )}
                  </IonCardContent>
                </IonRow>
              )}
            </IonItem>
          </IonList>

          <IonItemDivider color="clear" />

          <IonRow>
            <IonCol>
              <IonButton
                disabled={isDisableSaveBtn}
                expand="block"
                onClick={() => {
                  post({
                    id: "saved-song",
                    message: "Saved",
                    icon: musicalNoteOutline,
                  });
                  updateSong(song.id, {
                    ...song,
                    title: songTitle,
                    description: songDescription,
                    authors: songAuthors,
                    media: songMedia,
                    art,
                  });
                  handleRedirect();
                }}
              >
                Save
              </IonButton>
            </IonCol>
          </IonRow>
        </IonCol>
      </IonRow>
    </Content>
  );
};

export default EditForm;
